const TWO_DIGIT_YEAR_PIVOT = 70;

/**
 * Expands a 2-digit year the way a date input is conventionally read: `00`–`69` is treated as
 * 20xx and `70`–`99` as 19xx.
 */
export const expandTwoDigitYear = (year: number): number => (year < TWO_DIGIT_YEAR_PIVOT ? 2000 : 1900) + year;

export interface ParsedDateStringAnswer {
  month: number;
  day: number;
  year: number;
  normalized: string;
}

/**
 * Renders a date as the canonical `MM-DD-YYYY` answer shape, reading the calendar date in local
 * time. The inverse of {@link parseDateStringAnswer}.
 */
export const toDateStringAnswer = (date: Date): string =>
  [
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
    String(date.getFullYear()).padStart(4, "0"),
  ].join("-");

/**
 * Parses a `dateString` answer, accepting both `MM-DD-YYYY` and the 6-digit `MM-DD-YY` shape a
 * user may type or paste. Returns null when the value is not a real calendar date, which callers
 * treat as a format error.
 */
export const parseDateStringAnswer = (value: string): ParsedDateStringAnswer | null => {
  const match = /^(\d{2})-(\d{2})-(\d{2}|\d{4})$/.exec(value.trim());
  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const yearDigits = match[3] ?? "";
  const year = yearDigits.length === 2 ? expandTwoDigitYear(Number(yearDigits)) : Number(yearDigits);

  const parsed = new Date(year, month - 1, day);
  if (parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return null;

  return {
    month,
    day,
    year,
    normalized: `${match[1]}-${match[2]}-${String(year).padStart(4, "0")}`,
  };
};
